import React, { useCallback, useEffect, useRef, useState } from "react";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Chip as RebaseChip,
    CircularProgress as RebaseCircularProgress,
    Dialog as RebaseDialog,
    DialogActions as RebaseDialogActions,
    DialogContent as RebaseDialogContent,
    DialogTitle as RebaseDialogTitle,
    LoadingButton as RebaseLoadingButton,
    Markdown as RebaseMarkdown,
    Separator as RebaseSeparator,
    Typography as RebaseTypography,
    CheckIcon as RebaseCheckIcon,
    defaultBorderMixin,
    iconSize
} from "@rebasepro/ui";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgress = RebaseCircularProgress as React.ComponentType<any>;
const Dialog = RebaseDialog as React.ComponentType<any>;
const DialogActions = RebaseDialogActions as React.ComponentType<any>;
const DialogContent = RebaseDialogContent as React.ComponentType<any>;
const DialogTitle = RebaseDialogTitle as React.ComponentType<any>;
const LoadingButton = RebaseLoadingButton as React.ComponentType<any>;
const Markdown = RebaseMarkdown as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const CheckIcon = RebaseCheckIcon as React.ComponentType<any>;

/**
 * What the backend is working on while the admin waits, with the second at
 * which each stage typically starts.
 *
 * Estimates driven by a timer, not progress reported by the server — the
 * preview is one opaque POST that says nothing until it answers. Shown anyway
 * because a half-minute of nothing reads as a hang, which is exactly what this
 * screen used to do: "Aprobar y publicar" greyed the toolbar out and gave back
 * no sign of life until the job was already live.
 *
 * The offsets follow the real order in `buildApprovalDraft`: resolve the
 * employer (an Unbrand lookup that walks candidate domains when the offer has
 * no company yet), then the Gemini clean-and-translate pass.
 */
const PHASES = [
    { after: 0, label: "Buscando la empresa…" },
    { after: 10, label: "Limpiando y traduciendo con IA…" },
    { after: 30, label: "Terminando…" }
];

/** Past this, say why it is slow rather than leaving them guessing. */
const SLOW_AFTER_SECONDS = 25;

export type JobDraft = Record<string, any>;

type Phase =
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; job: JobDraft; companyId: string | null; llmProcessed: boolean };

/** A labelled block, rendered only when the LLM actually produced one. */
function Section({ title, body }: { title: string; body?: string | null }) {
    if (!body || !String(body).trim()) return null;
    return (
        <div className="flex flex-col gap-1">
            <Typography variant="label" color="secondary">{title}</Typography>
            <Markdown source={String(body)} size="small"/>
        </div>
    );
}

function Facts({ job }: { job: JobDraft }) {
    // Only what was actually extracted. A row of "—" placeholders makes an
    // offer look richer than it is, and the gaps are the point: they are what
    // the admin is deciding whether to accept.
    const facts: [string, unknown][] = [
        ["Ubicación", job.location_display],
        ["Modalidad", job.work_model],
        ["Jornada", job.employment_type],
        ["Contrato", job.contract_type],
        ["Seniority", job.seniority],
        ["Área", job.department],
        ["Salario", job.salary_display],
        ["Publicada", job.posted_at],
        ["Caduca", job.expires_at]
    ];
    const present = facts.filter(([, v]) => v !== null && v !== undefined && v !== "");
    if (present.length === 0) return null;

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
            {present.map(([label, value]) => (
                <div key={label} className="flex flex-col min-w-0">
                    <Typography variant="body2" color="secondary">{label}</Typography>
                    <Typography variant="body2" className="truncate">{String(value)}</Typography>
                </div>
            ))}
        </div>
    );
}

function ChipRow({ title, values }: { title: string; values?: unknown }) {
    if (!Array.isArray(values) || values.length === 0) return null;
    return (
        <div className="flex flex-col gap-1">
            <Typography variant="body2" color="secondary">{title}</Typography>
            <div className="flex flex-wrap gap-1">
                {values.map((v: unknown) => (
                    <Chip key={String(v)} size="small" colorScheme="grayLighter">{String(v)}</Chip>
                ))}
            </div>
        </div>
    );
}

/**
 * The draft itself, as the admin will see it published.
 *
 * Separate from the dialog so the non-null draft is a parameter rather than
 * something the reader has to prove from the phase union on every line.
 */
function DraftPreview({ ready, fallbackTitle }: {
    ready: { job: JobDraft; companyId: string | null; llmProcessed: boolean };
    fallbackTitle?: string | null;
}) {
    const { job, companyId, llmProcessed } = ready;
    return (
        <>
            {/* Said before the draft, not after: publishing an offer under the
                wrong employer — or under none at all — is the mistake this
                screen exists to catch, and it is invisible further down among
                the prose. */}
            {!companyId && (
                <Alert color="warning">
                    No hemos encontrado la ficha de esta empresa, así que se publicará
                    <strong> sin logo</strong> y sin enlace a su página. Puedes conectarla
                    después desde la vacante.
                </Alert>
            )}
            {!llmProcessed && (
                <Alert color="warning">
                    La IA no pudo reescribir esta oferta: lo de abajo es
                    <strong> el texto tal y como estaba en la web de origen</strong>,
                    sin traducir ni reordenar.
                </Alert>
            )}

            <div className="flex flex-col gap-1">
                <Typography variant="h6" className="leading-tight">
                    {job.title || fallbackTitle || "(sin título)"}
                </Typography>
                <Typography variant="body2" color="secondary">
                    {job.company_name || "(sin empresa)"}
                    {job.slug ? ` · /${job.slug}` : ""}
                </Typography>
            </div>

            <Separator/>
            <Facts job={job}/>

            {(Array.isArray(job.tags) || Array.isArray(job.skills)) && <Separator/>}
            <ChipRow title="Etiquetas" values={job.tags}/>
            <ChipRow title="Competencias" values={job.skills}/>
            <ChipRow title="Áreas ESG" values={job.sustainability_focus}/>

            <Separator/>
            <div className="flex flex-col gap-4">
                <Section title="Resumen" body={job.summary}/>
                <Section title="Descripción" body={job.description_md}/>
                <Section title="Responsabilidades" body={job.responsibilities_md}/>
                <Section title="Requisitos" body={job.requirements_md}/>
                <Section title="Beneficios" body={job.benefits_md}/>
                <Section title="Sobre la empresa" body={job.about_company_md}/>
            </div>
        </>
    );
}

/**
 * Approving used to be a leap: one click, half a minute of a disabled toolbar,
 * and then an offer was live on the public board — translated, re-titled and
 * re-classified by an LLM nobody had watched. The only way to see what it had
 * done was to go and look at the published job.
 *
 * This runs the same pipeline first and shows the result. The admin publishes
 * the draft they were shown, byte for byte: the approve call carries it back
 * rather than cleaning a second time, so what they approved is what ships —
 * and a second Gemini call, which could legitimately return something else, is
 * never made.
 */
export function ApprovePublishDialog({
    open,
    client,
    scrapedId,
    scrapedTitle,
    onClose,
    onPublished
}: {
    open: boolean;
    client: RebaseClient | null | undefined;
    scrapedId: string | null;
    scrapedTitle?: string | null;
    onClose: () => void;
    onPublished: (result: { promoted_job_id?: string; company_id?: string | null; message?: string }) => void;
}) {
    const [phase, setPhase] = useState<Phase>({ kind: "loading" });
    const [elapsed, setElapsed] = useState(0);
    const [publishing, setPublishing] = useState(false);

    // Aborted on close so "Cancelar" actually stops the request instead of
    // leaving it to finish into a dialog nobody is looking at.
    const abortRef = useRef<AbortController | null>(null);

    const call = useCallback(async (path: string, body?: unknown) => {
        const token = await (client as any)?.resolveToken?.();
        const base = `${(client as any)?.baseUrl ?? ""}${(client as any)?.apiPath ?? "/api"}`;
        const controller = new AbortController();
        abortRef.current = controller;
        const res = await fetch(`${base}${path}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: controller.signal
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.error || `Error ${res.status}`);
        return json;
    }, [client]);

    const runPreview = useCallback(async () => {
        if (!scrapedId) return;
        setPhase({ kind: "loading" });
        try {
            const json = await call(`/scraped-jobs/${scrapedId}/preview`);
            setPhase({
                kind: "ready",
                job: json.job ?? {},
                companyId: json.company_id ?? null,
                llmProcessed: json.llm_processed === true
            });
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            setPhase({ kind: "error", message: err?.message || "No se pudo generar la vista previa" });
        }
    }, [call, scrapedId]);

    useEffect(() => {
        if (!open) {
            abortRef.current?.abort();
            abortRef.current = null;
            setPhase({ kind: "loading" });
            setPublishing(false);
            return;
        }
        void runPreview();
        // Cleanup runs on close, handled by the branch above.
    }, [open, runPreview]);

    // One interval for the whole wait, so the counter advances even though the
    // request reports nothing until it lands.
    useEffect(() => {
        if (!open || phase.kind !== "loading") {
            setElapsed(0);
            return;
        }
        const started = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
        return () => clearInterval(timer);
    }, [open, phase.kind]);

    const phaseLabel = [...PHASES].reverse().find(p => elapsed >= p.after)?.label ?? PHASES[0].label;

    const publish = async () => {
        if (phase.kind !== "ready" || !scrapedId) return;
        setPublishing(true);
        try {
            const json = await call(`/scraped-jobs/${scrapedId}/approve`, {
                job: phase.job,
                company_id: phase.companyId,
                llm_processed: phase.llmProcessed
            });
            onPublished(json);
            onClose();
        } catch (err: any) {
            if (err?.name !== "AbortError") {
                setPhase({ kind: "error", message: err?.message || "No se pudo publicar" });
            }
        } finally {
            setPublishing(false);
        }
    };

    // Kept as the whole phase, not just `job`: the warnings below need
    // `companyId` and `llmProcessed`, and narrowing on a extracted field
    // does not carry back to the union.
    const ready = phase.kind === "ready" ? phase : null;
    const job = ready?.job ?? null;

    return (
        <Dialog
            open={open}
            onOpenChange={(next: boolean) => { if (!next) onClose(); }}
            maxWidth="3xl"
            fullWidth
        >
            <DialogTitle variant="h6" className="mb-1">
                Aprobar y publicar
            </DialogTitle>

            <DialogContent>
                <div className="flex flex-col gap-4 mt-1">
                    {phase.kind === "loading" && (
                        <div className={`flex items-center gap-3 text-sm p-4 border rounded-lg ${defaultBorderMixin}`}>
                            <CircularProgress size="smallest"/>
                            <div className="flex flex-col gap-1 min-w-0">
                                <Typography variant="body2">{phaseLabel}</Typography>
                                <Typography variant="body2" color="secondary">
                                    {elapsed}s
                                    {elapsed >= SLOW_AFTER_SECONDS &&
                                        " · traducir una oferta larga lleva su tiempo; no se ha publicado nada todavía"}
                                </Typography>
                            </div>
                        </div>
                    )}

                    {phase.kind === "error" && (
                        <Alert color="error">
                            <div className="flex flex-col gap-2 items-start">
                                <span>{phase.message}</span>
                                <Button size="small" variant="outlined" onClick={() => void runPreview()}>
                                    Reintentar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {ready && (
                        <DraftPreview ready={ready} fallbackTitle={scrapedTitle}/>
                    )}
                </div>
            </DialogContent>

            <DialogActions>
                <Typography variant="body2" color="secondary" className="grow hidden sm:block">
                    {job
                        ? "Se publicará tal cual en el tablón público."
                        : "Todavía no se ha publicado nada."}
                </Typography>
                <Button variant="text" onClick={onClose} disabled={publishing}>
                    Cancelar
                </Button>
                <LoadingButton
                    variant="filled"
                    color="primary"
                    loading={publishing}
                    disabled={!job}
                    onClick={() => void publish()}
                >
                    <CheckIcon size={iconSize.smallest}/> Publicar
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
